import { useState } from 'react';
import type {
  RulesetSource,
  RulesetSourceContributionLimit,
  RulesetSourceThreshold,
  RulesetSourceVersion,
} from '@signalvote/shared';
import { donorSourceCategoryLabel } from '@signalvote/shared';
import { useRulesets, useRulesetSource } from '@/hooks/queries/useFinanceRulesets';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { formatAmount } from '../utils';

/**
 * Read-only view of a jurisdiction's ruleset — the JSON file the compliance engine
 * loads, rendered as the tables a person can read: contribution limits, prohibited
 * sources, thresholds, the filing profile, and the remaining policy switches. Every
 * rule that carries a `_comment` note exposes it on demand, and the raw file is one
 * click away. Editing stays in git: the file ships with the API and is validated at
 * boot, so this page never writes.
 */
export function RulesPage() {
  const { data: settings, isLoading: settingsLoading } = useFinanceSettings();
  const { data: rulesets } = useRulesets();
  const [chosenCode, setChosenCode] = useState<string | null>(null);
  const code = chosenCode ?? settings?.rulesetCode ?? null;
  const { data: source, isLoading, isError } = useRulesetSource(code);

  const [versionChoice, setVersionChoice] = useState<{ code: string; date: string } | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const ruleset = source?.ruleset ?? null;
  const versions = ruleset ? sortedVersions(ruleset) : [];
  const inForce = versions.find((v) => v.effectiveDate <= getCampaignTodayString()) ?? null;
  // Nothing effective yet: fall back to the earliest declared version, labelled as such.
  const defaultVersionDate = inForce?.effectiveDate ?? versions[versions.length - 1]?.effectiveDate ?? null;
  const versionDate =
    versionChoice && versionChoice.code === code ? versionChoice.date : defaultVersionDate;
  const version = versions.find((v) => v.effectiveDate === versionDate) ?? null;
  const codeIsListed = !code || (rulesets ?? []).some((r) => r.code === code);

  return (
    <div className="px-1 pb-16">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold tracking-tight text-foreground">Rules</h1>
        <p className="mt-1.5 max-w-xl text-sm text-muted-foreground">
          What the compliance engine enforces for a jurisdiction, read straight from its ruleset
          file. Changes are made in the repository, not here.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="rules-jurisdiction" className="text-xs font-semibold text-muted-foreground">
            Jurisdiction
          </label>
          <select
            id="rules-jurisdiction"
            className="min-h-10 rounded-md border border-border bg-background px-3 text-sm"
            value={code ?? ''}
            onChange={(e) => setChosenCode(e.target.value || null)}
          >
            {!code && <option value="">— Select —</option>}
            {!codeIsListed && (
              <option value={code!} disabled>
                {code} (not in the list)
              </option>
            )}
            {(rulesets ?? []).map((r) => (
              <option key={r.code} value={r.code}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        {versions.length > 0 && code && (
          <div className="flex flex-col gap-1">
            <label htmlFor="rules-version" className="text-xs font-semibold text-muted-foreground">
              Version
            </label>
            <select
              id="rules-version"
              className="min-h-10 rounded-md border border-border bg-background px-3 text-sm"
              value={versionDate ?? ''}
              onChange={(e) => setVersionChoice({ code, date: e.target.value })}
            >
              {versions.map((v) => (
                <option key={v.effectiveDate} value={v.effectiveDate}>
                  Effective {v.effectiveDate}
                  {v.effectiveDate === defaultVersionDate
                    ? inForce
                      ? ' (in force today)'
                      : ' (earliest declared)'
                    : ''}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!code && settingsLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!code && !settingsLoading && (
        <p className="text-sm text-muted-foreground">
          Choose a jurisdiction to see the rules the app enforces for it.
        </p>
      )}
      {code && isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {code && isError && (
        <p className="text-sm text-destructive" role="alert">
          Could not load the ruleset for “{code}”. Check your access or try again.
        </p>
      )}
      {code && !isLoading && !isError && source === null && (
        <p className="text-sm text-muted-foreground">No ruleset file is loaded for “{code}”.</p>
      )}

      {ruleset && version && (
        <div className="space-y-8">
          {version._limitNote && (
            <p className="max-w-3xl whitespace-pre-wrap text-sm text-muted-foreground">
              {version._limitNote}
            </p>
          )}
          {/* Keyed by version so an open note never survives a version switch. */}
          <ContributionLimits key={`limits-${versionDate}`} limits={version.contributionLimits} />
          <ProhibitedSources version={version} />
          <Thresholds key={`thresholds-${versionDate}`} thresholds={version.thresholds} />
          <FilingProfile config={ruleset.config} />
          <PolicySwitches config={ruleset.config} />
          <Notes config={ruleset.config} />

          <section>
            <button
              type="button"
              className={linkButtonClass}
              onClick={() => setShowRaw((v) => !v)}
            >
              {showRaw ? 'Hide raw JSON' : 'Raw JSON'}
            </button>
            {showRaw && source && (
              <pre className="mt-2 max-h-[32rem] overflow-auto rounded-md border border-border bg-muted p-3 text-xs">
                {source.text}
              </pre>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

// ── Version selection ────────────────────────────────────────────────────────

function sortedVersions(ruleset: RulesetSource): RulesetSourceVersion[] {
  return [...ruleset.versions].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
}

// ── Sections ─────────────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: string }) {
  return <h2 className="mb-2 text-lg font-bold text-foreground">{children}</h2>;
}

const linkButtonClass =
  'inline-flex min-h-9 items-center rounded px-1 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const cellClass = 'px-3 py-2 align-top';
const headClass = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';
const tableClass = 'w-full border-collapse text-sm';
const rowClass = 'border-t border-border';

function ContributionLimits({ limits }: { limits: RulesetSourceContributionLimit[] }) {
  const [openNote, setOpenNote] = useState<number | null>(null);
  return (
    <section>
      <SectionHeading>Contribution limits</SectionHeading>
      {limits.length === 0 ? (
        <p className="text-sm text-muted-foreground">No contribution limits declared.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className={tableClass} aria-label="Contribution limits">
            <thead>
              <tr>
                <th className={headClass}>Donor type</th>
                <th className={headClass}>Office level</th>
                <th className={headClass}>Qualifier</th>
                <th className={headClass}>Limit</th>
                <th className={headClass}>Tracked</th>
                <th className={headClass}>Since</th>
                <th className={headClass}>
                  <span className="sr-only">Notes</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {limits.map((limit, i) => {
                const qualifier = limit.qualifierKey && limit.qualifierKey !== '*' ? limit.qualifierKey : '';
                return (
                  <NoteRows
                    key={i}
                    note={limit._comment}
                    open={openNote === i}
                    onToggle={() => setOpenNote(openNote === i ? null : i)}
                    label={`Why: ${limit.donorType}${qualifier ? ` ${qualifier}` : ''}`}
                    columns={7}
                  >
                    <td className={cellClass}>{limit.donorType}</td>
                    <td className={cellClass}>{limit.officeLevel === '*' ? 'Any' : limit.officeLevel}</td>
                    <td className={cellClass}>{qualifier || '—'}</td>
                    <td className={cellClass}>{limit.limit == null ? 'No limit' : formatAmount(limit.limit)}</td>
                    <td className={cellClass}>{limit.trackingPeriod}</td>
                    <td className={cellClass}>{limit.effectiveDate ?? '—'}</td>
                  </NoteRows>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ProhibitedSources({ version }: { version: RulesetSourceVersion }) {
  const prohibited = version.sourceRestrictions.filter((r) => r.prohibited);
  return (
    <section>
      <SectionHeading>Prohibited sources</SectionHeading>
      {prohibited.length === 0 ? (
        <p className="text-sm text-muted-foreground">No prohibited sources declared.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {prohibited.map((r) => {
            const source = r.sourceCategory ? donorSourceCategoryLabel(r.sourceCategory) : r.donorType;
            return (
              <li key={source}>
                <span className="font-semibold">{source}</span>
                <span className="text-muted-foreground"> — </span>
                <span className="text-muted-foreground">{r.reason}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Thresholds({ thresholds }: { thresholds: RulesetSourceThreshold[] }) {
  const [openNote, setOpenNote] = useState<number | null>(null);
  return (
    <section>
      <SectionHeading>Thresholds</SectionHeading>
      {thresholds.length === 0 ? (
        <p className="text-sm text-muted-foreground">No thresholds declared.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className={tableClass} aria-label="Thresholds">
            <thead>
              <tr>
                <th className={headClass}>Rule</th>
                <th className={headClass}>Amount</th>
                <th className={headClass}>Applies</th>
                <th className={headClass}>
                  <span className="sr-only">Notes</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {thresholds.map((t, i) => {
                const amount = t.value ?? t.aggregateThreshold;
                return (
                  <NoteRows
                    key={i}
                    note={t._comment}
                    open={openNote === i}
                    onToggle={() => setOpenNote(openNote === i ? null : i)}
                    label={`Why: ${t.type}`}
                    columns={4}
                  >
                    <td className={cellClass}>{t.type}</td>
                    <td className={cellClass}>{amount == null ? '—' : formatAmount(amount)}</td>
                    <td className={cellClass}>{describeThreshold(t) || '—'}</td>
                  </NoteRows>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The window and scope fields of a threshold, as one readable phrase. */
function describeThreshold(t: RulesetSourceThreshold): string {
  const parts: string[] = [];
  if (t.period) parts.push(humanize(t.period));
  if (t.aggregateThreshold != null && t.value == null) parts.push('aggregate');
  if (t.windowBusinessDays != null) parts.push(`within ${t.windowBusinessDays} business days`);
  if (t.windowDaysBeforeElection != null) {
    parts.push(
      t.windowEndDaysBeforeElection != null
        ? `from ${t.windowDaysBeforeElection} days before an election until ${t.windowEndDaysBeforeElection} days before`
        : `from ${t.windowDaysBeforeElection} days before an election`,
    );
  }
  if (t.daysBefore != null) parts.push(`${t.daysBefore} days before an election`);
  if (t.sessionStart || t.sessionEnd) parts.push(`session ${t.sessionStart ?? '?'} to ${t.sessionEnd ?? '?'}`);
  if (t.exemptionDays != null) parts.push(`${t.exemptionDays}-day exemption`);
  if (t.electionTypes?.length) parts.push(`elections: ${t.electionTypes.join(', ')}`);
  if (t.officeLevels?.length) parts.push(`offices: ${t.officeLevels.join(', ')}`);
  if (t.windowGatedNotice) parts.push('window-gated notice');
  if (t.overridable) parts.push('overridable');
  return parts.join('; ');
}

function FilingProfile({ config }: { config: RulesetSource['config'] }) {
  const profile = config.filingProfile;
  if (!profile) return null;
  const entries = Object.entries(profile).filter(([k]) => !k.startsWith('_'));
  return (
    <section>
      <SectionHeading>Filing profile</SectionHeading>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 border-t border-border py-1">
            <dt className="text-muted-foreground">{humanize(k)}</dt>
            <dd className="font-medium">{formatValue(v)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function PolicySwitches({ config }: { config: RulesetSource['config'] }) {
  const entries = Object.entries(config).filter(([k]) => !k.startsWith('_') && k !== 'filingProfile');
  const scalars = entries.filter(([, v]) => !isRecordList(v));
  const lists = entries.filter((e): e is [string, Record<string, unknown>[]] => isRecordList(e[1]));
  return (
    <section>
      <SectionHeading>Policy switches</SectionHeading>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        {scalars.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 border-t border-border py-1">
            <dt className="text-muted-foreground">{humanize(k)}</dt>
            <dd className="text-right font-medium">{formatValue(v)}</dd>
          </div>
        ))}
      </dl>
      {lists.map(([k, records]) => (
        <div key={k} className="mt-4">
          <h3 id={`policy-list-${k}`} className="text-sm font-semibold text-foreground">
            {humanize(k)}
          </h3>
          <ul aria-labelledby={`policy-list-${k}`} className="mt-1 space-y-1 text-sm">
            {records.map((record, i) => (
              <li key={i} className="border-t border-border py-1">
                {formatValue(record)}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

/** A non-empty array whose every element is a plain object — rendered one record per line. */
function isRecordList(v: unknown): v is Record<string, unknown>[] {
  return Array.isArray(v) && v.length > 0 && v.every((x) => x !== null && typeof x === 'object' && !Array.isArray(x));
}

/** Every `_`-prefixed string note on the config, plus those one level down (e.g. inside filingProfile). */
function collectNotes(config: RulesetSource['config']): Array<{ key: string; label: string; text: string }> {
  const notes: Array<{ key: string; label: string; text: string }> = [];
  for (const [k, v] of Object.entries(config)) {
    if (k.startsWith('_') && typeof v === 'string') {
      notes.push({ key: k, label: humanize(k.replace(/^_/, '')), text: v });
    } else if (v && typeof v === 'object' && !Array.isArray(v)) {
      for (const [nk, nv] of Object.entries(v as Record<string, unknown>)) {
        if (nk.startsWith('_') && typeof nv === 'string') {
          notes.push({
            key: `${k}.${nk}`,
            label: `${humanize(k)}: ${humanize(nk.replace(/^_/, '')).toLowerCase()}`,
            text: nv,
          });
        }
      }
    }
  }
  return notes;
}

function Notes({ config }: { config: RulesetSource['config'] }) {
  const notes = collectNotes(config);
  if (notes.length === 0) return null;
  return (
    <section>
      <SectionHeading>Notes from the ruleset</SectionHeading>
      <div className="space-y-2">
        {notes.map((n) => (
          <details key={n.key} className="rounded-md border border-border p-2 text-sm">
            <summary className="cursor-pointer font-medium">{n.label}</summary>
            <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{n.text}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────────────

/** A table row plus, when opened, a second row carrying the rule's note. */
function NoteRows({
  note,
  open,
  onToggle,
  label,
  columns,
  children,
}: {
  note: string | undefined;
  open: boolean;
  onToggle: () => void;
  label: string;
  columns: number;
  children: React.ReactNode;
}) {
  return (
    <>
      <tr className={rowClass}>
        {children}
        <td className={cellClass}>
          {note && (
            <button
              type="button"
              aria-label={label}
              aria-expanded={open}
              className={linkButtonClass}
              onClick={onToggle}
            >
              Why
            </button>
          )}
        </td>
      </tr>
      {note && open && (
        <tr>
          <td colSpan={columns} className="bg-muted px-3 py-2 text-xs text-muted-foreground whitespace-pre-wrap">
            {note}
          </td>
        </tr>
      )}
    </>
  );
}

/** camelCase → "Camel case". */
function humanize(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatValue(v: unknown): string {
  if (v == null) return 'Not set';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.map(formatValue).join(', ') : 'None';
  if (typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .filter(([k]) => !k.startsWith('_'))
      .map(([k, val]) => `${humanize(k)}: ${formatValue(val)}`)
      .join('; ');
  }
  return String(v);
}
